<template>
  <section class="experience-section modern-experience-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title modern-title">Professional Experience</h2>
        <p class="section-description modern-description">
          My journey through various roles and responsibilities
        </p>
        <div class="section-divider"></div>
      </div>

      <!-- Modern Timeline with Enhanced Cards -->
      <div class="experience-timeline">
        <div class="timeline-line"></div>

        <div
          v-for="(job, index) in experience"
          :key="index"
          class="experience-item modern-experience-item"
          :class="{
            'is-visible': isVisible[index],
            'is-hovered': job.isHovered,
            'timeline-left': index % 2 === 0,
            'timeline-right': index % 2 === 1
          }"
          @mouseenter="handleJobHover(job, true)"
          @mouseleave="handleJobHover(job, false)"
        >
          <!-- Timeline Dot -->
          <div class="timeline-dot modern-timeline-dot" :style="getTimelineDotStyle(index)">
            <v-icon color="white" size="20">{{ getJobIcon(job.title) }}</v-icon>
            <div class="dot-pulse" :style="getPulseStyle(index)"></div>
          </div>

          <!-- Experience Card -->
          <v-card
            class="experience-card modern-experience-card"
            elevation="0"
          >
            <!-- Enhanced Header with Company Info -->
            <div class="experience-header modern-experience-header">
              <div class="header-content">
                <div class="company-info">
                  <div class="company-logo" :style="getCompanyLogoStyle(job.company)">
                    <v-icon color="white" size="24">{{ getCompanyIcon(job.company) }}</v-icon>
                  </div>
                  <div class="company-details">
                    <h3 class="company-name modern-company-name">{{ job.company }}</h3>
                    <div class="job-period modern-job-period">
                      <v-icon small color="primary" class="period-icon">mdi-calendar-range</v-icon>
                      {{ job.period }}
                    </div>
                  </div>
                </div>

                <div class="experience-level">
                  <v-chip
                    small
                    :color="getExperienceLevelColor(job.title)"
                    class="level-chip"
                    text-color="white"
                  >
                    {{ getExperienceLevel(job.title) }}
                  </v-chip>
                </div>
              </div>

              <!-- Animated Background -->
              <div class="header-gradient" :style="getHeaderGradient(index)"></div>
            </div>

            <!-- Enhanced Card Content -->
            <v-card-title class="job-title-container modern-job-title">
              <div class="title-content">
                <h4 class="job-title">{{ job.title }}</h4>
                <div class="title-underline" :style="getTitleUnderlineStyle(index)"></div>
              </div>

              <!-- Job Status Indicator -->
              <div class="job-status">
                <div
                  class="status-indicator"
                  :class="{ 'current': job.period.includes('Present') }"
                  :title="job.period.includes('Present') ? 'Current Position' : 'Previous Position'"
                >
                  <div class="status-dot"></div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="experience-content modern-experience-content">
              <!-- Job Description (if available) -->
              <div v-if="job.description" class="job-description">
                <p>{{ job.description }}</p>
              </div>

              <!-- Responsibilities with Enhanced Styling -->
              <div class="responsibilities-section">
                <h5 class="responsibilities-title">Key Responsibilities & Achievements</h5>
                <ul class="responsibilities-list modern-responsibilities-list">
                  <li
                    v-for="(resp, idx) in job.responsibilities"
                    :key="idx"
                    class="responsibility-item modern-responsibility-item"
                    :style="getResponsibilityStyle(idx)"
                  >
                    <div class="responsibility-marker">
                      <v-icon small color="primary">mdi-check-circle</v-icon>
                    </div>
                    <span class="responsibility-text">{{ resp }}</span>
                  </li>
                </ul>
              </div>

              <!-- Skills Used (if available) -->
              <div v-if="job.skills" class="job-skills">
                <h5 class="skills-title">Technologies & Skills</h5>
                <div class="skills-chips">
                  <v-chip
                    v-for="(skill, skillIndex) in job.skills"
                    :key="skillIndex"
                    x-small
                    outlined
                    class="job-skill-chip"
                    :style="getSkillChipStyle(skillIndex)"
                  >
                    <v-icon left size="12" :color="getSkillColor(skill)">
                      {{ getSkillIcon(skill) }}
                    </v-icon>
                    {{ skill }}
                  </v-chip>
                </div>
              </div>

              <!-- Achievements/Metrics (if available) -->
              <div v-if="job.achievements" class="job-achievements">
                <h5 class="achievements-title">Key Achievements</h5>
                <div class="achievements-list">
                  <div
                    v-for="(achievement, achievementIndex) in job.achievements"
                    :key="achievementIndex"
                    class="achievement-item"
                  >
                    <v-icon small :color="achievement.color" class="achievement-icon">
                      {{ achievement.icon }}
                    </v-icon>
                    <span class="achievement-text">{{ achievement.text }}</span>
                  </div>
                </div>
              </div>

              <!-- Duration Badge -->
              <div class="duration-info">
                <v-chip
                  x-small
                  color="info"
                  outlined
                  class="duration-chip"
                >
                  <v-icon left size="12">mdi-clock-outline</v-icon>
                  {{ calculateDuration(job.period) }}
                </v-chip>
              </div>
            </v-card-text>

            <!-- Card Glow Effect -->
            <div class="card-glow" :style="getCardGlowStyle(index)"></div>
          </v-card>
        </div>
      </div>

      <!-- Experience Summary Stats -->
      <div class="experience-summary">
        <div class="summary-container">
          <div class="summary-stat">
            <div class="stat-number">{{ getTotalExperience() }}</div>
            <div class="stat-label">Years Experience</div>
          </div>
          <div class="summary-stat">
            <div class="stat-number">{{ experience.length }}</div>
            <div class="stat-label">Positions</div>
          </div>
          <div class="summary-stat">
            <div class="stat-number">{{ getTotalCompanies() }}</div>
            <div class="stat-label">Companies</div>
          </div>
        </div>
      </div>

      <!-- Career Growth Visualization -->
      <div class="career-growth">
        <h3 class="growth-title">Career Progression</h3>
        <div class="growth-chart">
          <div
            v-for="(job, index) in experience"
            :key="`growth-${index}`"
            class="growth-step"
            :style="getGrowthStepStyle(index)"
          >
            <div class="growth-bar" :style="getGrowthBarStyle(index)"></div>
            <div class="growth-label">{{ getJobLevel(job.title) }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ProfessionalExperienceSection",
  props: {
    experience: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isVisible: [],
      colors: ['#4361ee', '#f72585', '#4cc9f0', '#7209b7', '#f77f00', '#06ffa5'],
    };
  },
  methods: {
    handleJobHover(job, isHovered) {
      this.$set(job, 'isHovered', isHovered);
    },

    handleScroll() {
      const items = document.querySelectorAll('.experience-item');
      items.forEach((item, index) => {
        if (this.isInViewport(item)) {
          this.$set(this.isVisible, index, true);
        }
      });
    },

    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
    },

    getJobIcon(title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes('lead') || titleLower.includes('senior')) return 'mdi-account-tie';
      if (titleLower.includes('frontend') || titleLower.includes('front-end')) return 'mdi-monitor';
      if (titleLower.includes('fullstack') || titleLower.includes('full-stack')) return 'mdi-layers';
      if (titleLower.includes('backend') || titleLower.includes('back-end')) return 'mdi-server';
      if (titleLower.includes('developer')) return 'mdi-code-tags';
      return 'mdi-briefcase';
    },

    getCompanyIcon(company) {
      const companyLower = company.toLowerCase();
      if (companyLower.includes('tech')) return 'mdi-chip';
      if (companyLower.includes('soft')) return 'mdi-application';
      if (companyLower.includes('dev')) return 'mdi-code-braces';
      return 'mdi-office-building';
    },

    getExperienceLevel(title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes('lead') || titleLower.includes('principal')) return 'Lead';
      if (titleLower.includes('senior')) return 'Senior';
      if (titleLower.includes('junior')) return 'Junior';
      if (titleLower.includes('intern')) return 'Intern';
      return 'Mid-Level';
    },

    getExperienceLevelColor(title) {
      const level = this.getExperienceLevel(title);
      const colorMap = {
        'Lead': 'deep-purple',
        'Senior': 'primary',
        'Mid-Level': 'teal',
        'Junior': 'green',
        'Intern': 'orange'
      };
      return colorMap[level] || 'grey';
    },

    getJobLevel(title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes('lead') || titleLower.includes('principal')) return 5;
      if (titleLower.includes('senior')) return 4;
      if (titleLower.includes('junior')) return 2;
      if (titleLower.includes('intern')) return 1;
      return 3; // Mid-level
    },

    getSkillColor(skill) {
      const skillColors = {
        'Vue.js': '#42B883',
        'React': '#61DAFB',
        'Node.js': '#68A063',
        'JavaScript': '#F0DB4F',
        'TypeScript': '#3178C6',
        'Python': '#3572A5',
        'Leadership': '#FF6B6B',
        'Agile': '#4ECDC4'
      };
      return skillColors[skill] || '#666';
    },

    getSkillIcon(skill) {
      const skillIcons = {
        'Vue.js': 'mdi-vuejs',
        'React': 'mdi-react',
        'Node.js': 'mdi-nodejs',
        'JavaScript': 'mdi-language-javascript',
        'TypeScript': 'mdi-language-typescript',
        'Python': 'mdi-language-python',
        'Leadership': 'mdi-account-group',
        'Agile': 'mdi-refresh'
      };
      return skillIcons[skill] || 'mdi-code-tags';
    },

    getTimelineDotStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        backgroundColor: color,
        borderColor: color,
        boxShadow: `0 0 20px ${color}40`
      };
    },

    getPulseStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        backgroundColor: color
      };
    },

    getCompanyLogoStyle(company) {
      // You could map actual company colors here
      const colors = ['#4361ee', '#f72585', '#4cc9f0', '#7209b7'];
      const colorIndex = company.length % colors.length;
      return {
        backgroundColor: colors[colorIndex]
      };
    },

    getHeaderGradient(index) {
      const color1 = this.colors[index % this.colors.length];
      const color2 = this.colors[(index + 1) % this.colors.length];
      return {
        background: `linear-gradient(135deg, ${color1}20, ${color2}10)`
      };
    },

    getTitleUnderlineStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        backgroundColor: color
      };
    },

    getResponsibilityStyle(index) {
      return {
        '--delay': `${index * 0.1}s`
      };
    },

    getSkillChipStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        borderColor: color,
        color: color
      };
    },

    getCardGlowStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        background: `radial-gradient(circle at center, ${color}15, transparent 70%)`,
        opacity: 0
      };
    },

    getGrowthStepStyle(index) {
      const level = this.getJobLevel(this.experience[index].title);
      return {
        width: `${level * 20}%`
      };
    },

    getGrowthBarStyle(index) {
      const color = this.colors[index % this.colors.length];
      const level = this.getJobLevel(this.experience[index].title);
      return {
        backgroundColor: color,
        height: `${level * 8}px`
      };
    },

    calculateDuration(period) {
      // Simple duration calculation
      const match = period.match(/(\d{4})\s*-\s*(\d{4}|Present)/);
      if (match) {
        const startYear = parseInt(match[1]);
        const endYear = match[2] === 'Present' ? new Date().getFullYear() : parseInt(match[2]);
        const duration = endYear - startYear;
        return duration <= 1 ? '< 1 year' : `${duration} years`;
      }
      return period;
    },

    getTotalExperience() {
      // Calculate total years of experience
      let totalYears = 0;
      this.experience.forEach(job => {
        const match = job.period.match(/(\d{4})\s*-\s*(\d{4}|Present)/);
        if (match) {
          const startYear = parseInt(match[1]);
          const endYear = match[2] === 'Present' ? new Date().getFullYear() : parseInt(match[2]);
          totalYears += (endYear - startYear);
        }
      });
      return totalYears || '3+';
    },

    getTotalCompanies() {
      const uniqueCompanies = new Set(this.experience.map(job => job.company));
      return uniqueCompanies.size;
    }
  },

  mounted() {
    // Initialize visibility states
    this.isVisible = new Array(this.experience.length).fill(false);

    // Initialize hover states
    this.experience.forEach(job => {
      this.$set(job, 'isHovered', false);
    });

    // Add scroll listener for visibility detection
    window.addEventListener('scroll', this.handleScroll);
    this.$nextTick(this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.experience-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 20% 30%, rgba(67, 97, 238, 0.08), transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(247, 37, 133, 0.06), transparent 50%);
    pointer-events: none;
  }
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.modern-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, $accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.section-divider {
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, $primary, $accent, #f72585);
  margin: 0 auto;
  border-radius: 2px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, $primary, $accent, #f72585);
    border-radius: 4px;
    opacity: 0.3;
    filter: blur(4px);
  }
}

// Modern Experience Timeline
.experience-timeline {
  position: relative;
  margin: 2rem 0;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to bottom, $primary, $accent, #f72585);
  transform: translateX(-50%);
  border-radius: 2px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -4px;
    width: 12px;
    height: 100%;
    background: linear-gradient(to bottom, rgba(67, 97, 238, 0.2), rgba(247, 37, 133, 0.2));
    border-radius: 6px;
    filter: blur(8px);
  }
}

.experience-item {
  position: relative;
  margin: 4rem 0;
  opacity: 1;
  transform: translateY(0);
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &.timeline-left {
    .experience-card {
      margin-right: 50%;
      padding-right: 3rem;
    }
  }

  &.timeline-right {
    .experience-card {
      margin-left: 50%;
      padding-left: 3rem;
    }
  }

  &.is-hovered {
    .experience-card {
      transform: scale(1.02);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

      .card-glow {
        opacity: 1 !important;
      }
    }

    .timeline-dot {
      transform: scale(1.2);

      .dot-pulse {
        animation: pulse 2s infinite;
      }
    }
  }
}

.timeline-dot {
  position: absolute;
  left: 50%;
  top: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateX(-50%);
  border: 4px solid;
  z-index: 10;
  transition: all 0.3s ease;

  .dot-pulse {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    opacity: 0.7;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.3;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

// Modern Experience Cards
.modern-experience-card {
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;

  .theme--light & {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow: 0 8px 32px rgba(67, 97, 238, 0.08);
  }
}

.modern-experience-header {
  position: relative;
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .theme--light & {
    border-color: rgba(67, 97, 238, 0.1);
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  z-index: 2;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.company-logo {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.company-details {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.modern-company-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;
  margin: 0;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.modern-job-period {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.experience-level {
  .level-chip {
    font-weight: 600;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.header-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 24px 24px 0 0;
  opacity: 0.1;
  z-index: 1;
}

.modern-job-title {
  padding: 1.5rem 2rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-content {
  position: relative;
}

.job-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.5rem 0;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.title-underline {
  width: 60px;
  height: 3px;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.job-status {
  .status-indicator {
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;

    .theme--light & {
      border-color: rgba(67, 97, 238, 0.3);
    }

    &.current {
      border-color: #4caf50;

      .status-dot {
        background: #4caf50;
        animation: currentPulse 2s infinite;
      }
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);

      .theme--light & {
        background: rgba(67, 97, 238, 0.5);
      }
    }
  }
}

@keyframes currentPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.modern-experience-content {
  padding: 0 2rem 2rem;
}

.job-description {
  margin-bottom: 1.5rem;

  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    font-size: 1rem;

    .theme--light & {
      color: rgba(73, 80, 87, 0.8);
    }
  }
}

.responsibilities-section {
  margin-bottom: 2rem;
}

.responsibilities-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }
}

.modern-responsibilities-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.modern-responsibility-item {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  margin-bottom: 0.8rem;
  opacity: 0;
  transform: translateX(-20px);
  animation: slideInLeft 0.6s ease forwards;
  animation-delay: var(--delay, 0s);

  .responsibility-marker {
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  .responsibility-text {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.5;
    font-size: 0.95rem;

    .theme--light & {
      color: rgba(73, 80, 87, 0.8);
    }
  }
}

@keyframes slideInLeft {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.job-skills {
  margin-bottom: 1.5rem;
}

.skills-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.8rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }
}

.skills-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.job-skill-chip {
  font-size: 0.75rem;
  height: 26px;
  border-radius: 13px;
  transition: all 0.2s ease;
  background: rgba(67, 97, 238, 0.12) !important;
  color: $primary !important;
  border: 1px solid $primary !important;

  .theme--light & {
    background: rgba(67, 97, 238, 0.08) !important;
    color: $primary !important;
  }

  &:hover {
    transform: scale(1.05);
    background: rgba(67, 97, 238, 0.2) !important;
  }

  .v-icon {
    margin-left: -4px !important;
  }
}

.job-achievements {
  margin-bottom: 1.5rem;
}

.achievements-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.8rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }
}

.achievements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.achievement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.achievement-text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.duration-info {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.duration-chip {
  font-size: 0.7rem;
  font-weight: 600;
}

.card-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  transition: all 0.5s ease;
  pointer-events: none;
  z-index: -1;
}

// Experience Summary
.experience-summary {
  text-align: center;
  margin: 4rem 0 2rem;
}

.summary-container {
  display: flex;
  justify-content: center;
  gap: 4rem;
}

.summary-stat {
  text-align: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: $primary;
  line-height: 1;
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

// Career Growth
.career-growth {
  text-align: center;
  margin-top: 3rem;
}

.growth-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.growth-chart {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;
  height: 100px;
}

.growth-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.growth-bar {
  border-radius: 4px 4px 0 0;
  transition: all 0.6s ease;
  min-width: 40px;
}

.growth-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

// Responsive Design
@media (max-width: $breakpoint-lg) {
  .timeline-line {
    left: 2rem;
  }

  .timeline-dot {
    left: 2rem;
  }

  .experience-item {
    &.timeline-left,
    &.timeline-right {
      .experience-card {
        margin-left: 5rem;
        margin-right: 0;
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
}

@media (max-width: $breakpoint-md) {
  .modern-title {
    font-size: 2.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .modern-job-title {
    padding: 1.2rem 1.5rem 0.8rem;
  }

  .modern-experience-content {
    padding: 0 1.5rem 1.5rem;
  }

  .summary-container {
    gap: 2rem;
  }

  .stat-number {
    font-size: 2rem;
  }
}

@media (max-width: $breakpoint-xs) {
  .experience-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .modern-title {
    font-size: 2rem;
  }

  .modern-description {
    font-size: 1rem;
  }

  .timeline-line {
    left: 1.5rem;
  }

  .timeline-dot {
    left: 1.5rem;
    width: 50px;
    height: 50px;
  }

  .experience-item {
    &.timeline-left,
    &.timeline-right {
      .experience-card {
        margin-left: 4rem;
      }
    }
  }

  .modern-experience-header {
    padding: 1.5rem;
  }

  .modern-job-title {
    padding: 1rem 1.5rem 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .modern-experience-content {
    padding: 0 1.5rem 1.5rem;
  }

  .summary-container {
    flex-direction: column;
    gap: 1.5rem;
  }

  .stat-number {
    font-size: 1.8rem;
  }

  .growth-chart {
    gap: 0.5rem;
  }

  .growth-bar {
    min-width: 30px;
  }
}

// Dark/Light theme specific adjustments
.theme--light {
  .timeline-line {
    background: linear-gradient(to bottom, rgba(67, 97, 238, 0.8), rgba(247, 37, 133, 0.6));

    &::before {
      background: linear-gradient(to bottom, rgba(67, 97, 238, 0.1), rgba(247, 37, 133, 0.1));
    }
  }
}
</style>